/**
 * Pure display helpers + constants for the admin Audit Log panel (Flow J).
 *
 * No React here — the action-kind list (derived from the generated audit
 * taxonomy), the outcome → label + semantic-token mapping, the time-range
 * presets, time formatting, and best-effort Resource / Source-IP derivation
 * from the audit ``details`` payload. Kept separate so they're unit-testable
 * without the DOM.
 *
 * Cross-references:
 *   - Flow J §"Action kinds" / §"Outcome semantics" / §"Time-range chip".
 *   - REQ-UI-022 (Audit Log surface).
 */
import { AUDIT_EVENT_TYPES_BY_CATEGORY, type AuditEventCategory } from "@/audit/audit-taxonomy.gen";
import type { AuditEventItem, AuditOutcomeFilter } from "@/audit/use-audit-events";

/**
 * Operator-facing label per ``meridian.v1.audit.*`` category — the group
 * headers of the action-kind picker. Keyed by the generated category type, so
 * a category added to the taxonomy fails ``tsc`` here until it has a label
 * rather than rendering as its raw message name (REQ-UI-022; ADR-010 §2).
 */
export const AUDIT_CATEGORY_LABELS: Record<AuditEventCategory, string> = {
  AuthenticationEvent: "Authentication",
  AuthorizationEvent: "Authorization",
  DataAccessEvent: "Data access",
  ConfigurationChangeEvent: "Configuration change",
  ConnectorEvent: "Connector",
  CdsEvent: "Cross-domain transfer",
  CertLifecycleEvent: "Certificate lifecycle",
  MtlsEvent: "Internal mTLS",
  DataLifecycleEvent: "Data lifecycle",
  HaLeaseEvent: "HA lease",
};

/** One group of the action-kind picker: a category and the kinds it offers. */
export interface AuditActionKindGroup {
  category: AuditEventCategory;
  /** ``AUDIT_CATEGORY_LABELS[category]``. */
  label: string;
  kinds: readonly string[];
}

/**
 * Whether an event type can match the BFF's exact ``event_type`` filter.
 *
 * ``peer.<state>`` and its five siblings are runtime placeholders — the
 * peer / provider state routes substitute the requested state — so no stored
 * row ever carries the literal and an exact filter on it returns nothing.
 * Those families stay reachable by prefix through the free-text box, which
 * matches ``event_type`` by ILIKE; expanding them here would need the state
 * enum as a typed contract first (§6 open item, 2026-10-19).
 */
function isExactlyMatchable(eventType: string): boolean {
  return !eventType.includes("<");
}

/**
 * The action-kind picker's groups (Flow J §"Action kinds"), **derived** from
 * the generated taxonomy rather than authored: every exactly-matchable event
 * type the system emits, grouped by its ``meridian.v1.audit.*`` category, in
 * taxonomy order. The server filters on exact ``event_type`` and has no
 * allow-list of its own, so this list is the whole gate on what an admin can
 * pick — and a hand-authored list is a list that drifts.
 *
 * It did. Until 2026-10-19 this was a literal written against a proposed
 * taxonomy that never shipped: eleven of its nineteen entries had no emit
 * site (``user.token.refresh``, ``connector.auth.success`` / ``.failure``,
 * ``platform.disabled``, ``user.password.reset``, …), so picking one was a
 * filter that silently matched nothing — the near-miss trap migration 046
 * documents for the sensitivity view — and it lacked every session-lifecycle
 * type that is emitted, including ``user.session.refresh_failed``, the event
 * that tells a Keycloak outage from an ended SSO session (#1221).
 *
 * Deriving the list also settles the entry that was added by hand on
 * 2026-10-06 and is worth keeping in mind: ``connector.message_quarantined``,
 * the one quarantine event the system emits (``AuditedQuarantineSink``), and
 * the source the Platform pane's RECENT ERRORS section reads. That section is
 * scoped to one producer and the emitter throttles to one event per
 * (producer, reason, outcome) per interface per 60 s, so this filter is the
 * only way to see the system-wide picture — and a quarantine whose frame was
 * refused before it named a producer is attributed to no platform, so it
 * appears HERE and in no platform's section at all (REQ-UI-049,
 * REQ-CONN-024). Omitted until 2026-10-06, reachable only by knowing to type
 * it into the free-text box; now it cannot be omitted, because nothing is.
 *
 * ``audit-taxonomy.gen.ts`` is held to the Python constant by
 * ``tools/check_audit_taxonomy.py`` (the ``audit-taxonomy-lint`` CI job);
 * ``audit-taxonomy.test.ts`` pins this derivation.
 */
export const AUDIT_ACTION_KIND_GROUPS: readonly AuditActionKindGroup[] =
  // ``Object.keys`` is typed ``string[]``; the object is a closed ``as const``
  // literal, so its keys are exactly the category union.
  (Object.keys(AUDIT_EVENT_TYPES_BY_CATEGORY) as AuditEventCategory[])
    .map((category) => ({
      category,
      label: AUDIT_CATEGORY_LABELS[category],
      kinds: AUDIT_EVENT_TYPES_BY_CATEGORY[category].filter(isExactlyMatchable),
    }))
    .filter((group) => group.kinds.length > 0);

/**
 * The flat action-kind list — ``AUDIT_ACTION_KIND_GROUPS`` in order. Drives
 * the action-kind multi-select; the server filters on exact ``event_type``.
 */
export const AUDIT_ACTION_KINDS: readonly string[] = AUDIT_ACTION_KIND_GROUPS.flatMap(
  (group) => group.kinds,
);

/** Outcome display label + semantic-token text-color class. */
export interface OutcomeMeta {
  label: string;
  className: string;
}

/**
 * Map the stored ``OUTCOME_*`` enum value to the Flow J label +
 * semantic token. Success / Failure / Denied are visually distinct per
 * the Flow J acceptance criteria (§6 decision 2026-06-08: Success →
 * ``--positive``, Failure → ``--destructive``, Denied → ``--warning``).
 */
export function outcomeMeta(outcome: string): OutcomeMeta {
  switch (outcome) {
    case "OUTCOME_ACCEPT":
      return { label: "Success", className: "text-positive" };
    case "OUTCOME_ERROR":
      return { label: "Failure", className: "text-destructive" };
    case "OUTCOME_REJECT":
      return { label: "Denied", className: "text-warning" };
    default:
      return { label: outcome, className: "text-text-secondary" };
  }
}

/** The outcome filter values + their dropdown labels (Flow J). */
export const OUTCOME_FILTERS: readonly AuditOutcomeFilter[] = [
  "all",
  "success",
  "failure",
  "denied",
];

export const OUTCOME_FILTER_LABELS: Record<AuditOutcomeFilter, string> = {
  all: "All outcomes",
  success: "Success",
  failure: "Failure",
  denied: "Denied",
};

/** Quick time-range presets (Flow J §"Time-range chip"). */
export interface TimeRangePreset {
  id: string;
  label: string;
  /** Hours back from now. */
  hours: number;
}

export const TIME_RANGE_PRESETS: readonly TimeRangePreset[] = [
  { id: "1h", label: "Last 1 hour", hours: 1 },
  { id: "24h", label: "Last 24 hours", hours: 24 },
  { id: "7d", label: "Last 7 days", hours: 24 * 7 },
  { id: "30d", label: "Last 30 days", hours: 24 * 30 },
];

/** Default landing range (Flow J Frame 1 — "Last 24 hours"). */
export const DEFAULT_TIME_RANGE_ID = "24h";

/**
 * The panel-owned filter view-model (UI state, pre-wire). Lives in this
 * non-component module so the filter bar stays a pure component export
 * (react-refresh/only-export-components).
 */
export interface AuditFilterState {
  timeRangeId: string;
  actor: string;
  actionKind: string[];
  outcome: AuditOutcomeFilter;
  q: string;
  includeConnectorAuth: boolean;
}

/** Flow J Frame 1 landing defaults (Last 24h, no filters). */
export const DEFAULT_AUDIT_FILTER: AuditFilterState = {
  timeRangeId: DEFAULT_TIME_RANGE_ID,
  actor: "",
  actionKind: [],
  outcome: "all",
  q: "",
  includeConnectorAuth: false,
};

/** Compute the ISO ``start`` for a preset id relative to ``nowMs``. */
export function presetStartIso(timeRangeId: string, nowMs: number): string | null {
  const preset = TIME_RANGE_PRESETS.find((p) => p.id === timeRangeId);
  if (preset === undefined) return null;
  return new Date(nowMs - preset.hours * 3_600_000).toISOString();
}

/** Format an ISO timestamp as ``YYYY-MM-DD HH:MM:SS`` UTC (second precision). */
export function formatAuditTime(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  const pad = (n: number): string => String(n).padStart(2, "0");
  return (
    `${parsed.getUTCFullYear()}-${pad(parsed.getUTCMonth() + 1)}-${pad(parsed.getUTCDate())} ` +
    `${pad(parsed.getUTCHours())}:${pad(parsed.getUTCMinutes())}:${pad(parsed.getUTCSeconds())}`
  );
}

/**
 * Best-effort "Resource" cell from the audit ``details`` payload.
 *
 * ``details`` is the camelCase audit message (Vector ``shape_audit``);
 * the resource identity lives in event-type-specific fields, so we probe
 * the known carriers in priority order. Returns an em-dash when none is
 * present (e.g. a bare login event).
 */
export function deriveResource(details: AuditEventItem["details"]): string {
  for (const key of ["targetId", "resource", "producerId", "aggregatorId", "meridianTransferId"]) {
    const value = details[key];
    if (typeof value === "string" && value.length > 0) return value;
  }
  return "—";
}

/** Best-effort "Source IP" from the audit ``details`` payload (auth events). */
export function deriveSourceIp(details: AuditEventItem["details"]): string {
  const value = details["clientIp"] ?? details["sourceIp"];
  return typeof value === "string" && value.length > 0 ? value : "—";
}

// ---- Sort (server-side; DB-backed columns only) ------------------

/**
 * The BFF ``sort_by`` set (``list_events_api_admin_audit_events_get``).
 * Only DB-backed columns are sortable — Resource / Source IP are derived
 * from the ``details`` JSONB and have no ``sort_by`` mapping. Sending a
 * value outside this union is a 422, so the column model must only offer
 * sort on these (the locked-decision constraint).
 */
export type AuditSortBy =
  "time" | "event_type" | "actor" | "actor_kind" | "outcome" | "service" | "correlation_id";

/** Sort direction (BFF ``sort_dir``). */
export type AuditSortDir = "asc" | "desc";

/** Flow J Frame 1 default: newest-first by Time. */
export const DEFAULT_AUDIT_SORT_BY: AuditSortBy = "time";
export const DEFAULT_AUDIT_SORT_DIR: AuditSortDir = "desc";

// ---- Column model ------------------------------------------------

/**
 * One Audit Log table column.
 *
 * Pure metadata (no React) so the column set stays unit-testable and the
 * ``audit-display`` module stays DOM-free — the panel maps ``key`` → a
 * cell renderer (mirrors the Appaloosa ``ColumnDef`` shape, adapted to
 * Meridian's storyboard-silent-on-sort constraint). ``sortKey`` is the
 * BFF ``sort_by`` value; omit it for derived (Resource / Source IP)
 * columns so they render no sort affordance and are never clickable.
 */
export interface AuditColumnDef {
  /** Stable column id (also the localStorage token + cell-render switch). */
  key: string;
  /** Header label. */
  label: string;
  /** BFF ``sort_by`` value; omit ⇒ unsortable (derived column). */
  sortKey?: AuditSortBy;
  /** Whether the column shows on first load (before any localStorage state). */
  defaultVisible: boolean;
}

/**
 * The Audit Log column set (Flow J Frame 1 table + the three default-
 * hidden DB columns the cog can reveal — the locked column-model
 * decision). Order here is the rendered left-to-right order.
 *
 * Default-visible (Flow J Frame 1): Time, Actor, Action, Resource,
 * Outcome, Source IP. Default-hidden (toggleable): Service, Actor Kind,
 * Correlation ID — all DB-backed ⇒ sortable.
 *
 * Sortable → ``sort_by`` mapping: Time→``time``, Actor→``actor``,
 * Action→``event_type``, Outcome→``outcome``, Service→``service``,
 * Actor Kind→``actor_kind``, Correlation ID→``correlation_id``. Resource
 * + Source IP are derived from ``details`` (no ``sort_by``).
 */
export const AUDIT_COLUMNS: readonly AuditColumnDef[] = [
  { key: "time", label: "Time (UTC)", sortKey: "time", defaultVisible: true },
  { key: "actor", label: "Actor", sortKey: "actor", defaultVisible: true },
  { key: "event_type", label: "Action", sortKey: "event_type", defaultVisible: true },
  { key: "resource", label: "Resource", defaultVisible: true },
  { key: "outcome", label: "Outcome", sortKey: "outcome", defaultVisible: true },
  { key: "source_ip", label: "Source IP", defaultVisible: true },
  { key: "service", label: "Service", sortKey: "service", defaultVisible: false },
  { key: "actor_kind", label: "Actor Kind", sortKey: "actor_kind", defaultVisible: false },
  {
    key: "correlation_id",
    label: "Correlation ID",
    sortKey: "correlation_id",
    defaultVisible: false,
  },
];

/** localStorage key for the operator's chosen column visibility (locked). */
export const AUDIT_VISIBLE_COLS_KEY = "meridian-audit-visible-cols";

/** The default-visible column keys (the Flow J Frame 1 set). */
export function defaultVisibleColumnKeys(): Set<string> {
  return new Set(AUDIT_COLUMNS.filter((c) => c.defaultVisible).map((c) => c.key));
}

/**
 * Load the persisted visible-column set from ``localStorage``, falling
 * back to the Flow J Frame 1 defaults.
 *
 * Defensive at the storage trust boundary (coding-standards §2.2): a
 * malformed / empty / all-unknown blob falls back to the defaults. Keys
 * are intersected with the known column set so a stale key from a removed
 * column is dropped silently. Honours the **min-1-column** invariant —
 * an empty stored set is treated as absent.
 */
export function loadVisibleColumnKeys(): Set<string> {
  const known = new Set(AUDIT_COLUMNS.map((c) => c.key));
  try {
    const raw = localStorage.getItem(AUDIT_VISIBLE_COLS_KEY);
    if (raw !== null) {
      const parsed: unknown = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        const kept = parsed.filter((k): k is string => typeof k === "string" && known.has(k));
        if (kept.length > 0) return new Set(kept);
      }
    }
  } catch {
    // Corrupt JSON / storage unavailable — fall through to defaults.
  }
  return defaultVisibleColumnKeys();
}

/** Persist the visible-column set; best-effort (ignores storage failures). */
export function saveVisibleColumnKeys(keys: ReadonlySet<string>): void {
  try {
    localStorage.setItem(AUDIT_VISIBLE_COLS_KEY, JSON.stringify([...keys]));
  } catch {
    // Storage unavailable (private mode / quota) — visibility is
    // session-only this run; not worth surfacing to the operator.
  }
}

/**
 * Toggle one column key in a visible set, enforcing the **min-1-column**
 * invariant: the last visible column cannot be hidden (the locked
 * decision). Returns a new set (pure — caller persists + sets state).
 */
export function toggleColumnKey(current: ReadonlySet<string>, key: string): Set<string> {
  const next = new Set(current);
  if (next.has(key)) {
    if (next.size > 1) next.delete(key); // never empty the table
  } else {
    next.add(key);
  }
  return next;
}

/**
 * Next sort state for a header activation (Appaloosa ``handleSort``
 * semantics): clicking the active column flips direction; clicking a new
 * column selects it ascending. Pure — returns the ``(by, dir)`` pair.
 */
export function nextSort(
  current: { by: AuditSortBy; dir: AuditSortDir },
  clicked: AuditSortBy,
): { by: AuditSortBy; dir: AuditSortDir } {
  if (current.by === clicked) {
    return { by: clicked, dir: current.dir === "asc" ? "desc" : "asc" };
  }
  return { by: clicked, dir: "asc" };
}
